import { auth, firestore } from './firebase';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  addDoc,
  orderBy,
  limit,
  runTransaction,
  serverTimestamp,
  arrayUnion,
  deleteDoc,
  increment,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { rememberProductPhotos, rememberSitePhotos, rememberedSitePhotos } from './photos';
import { itemNameOf, orderLines, sortProducts, stockChange, stockMovement } from './catalogue';
import { setUpCatalogue } from './catalogueApi';

//
// ——— PRODUCTS ——————————————————————————————————————————————————————————
//
// The catalogue: one `products` document per product, holding its names,
// price, stock, photos and tax treatment. See catalogue.js.

/** Who is making a change, for the stock history. */
const actor = () => auth.currentUser?.email || 'dashboard';

const productRow = (snap) => ({ ...snap.data(), id: snap.id });

async function readAllProducts() {
  const snap = await getDocs(collection(firestore, 'products'));
  const rows = snap.docs.map(productRow);
  // So the cart and checkout can draw the right photos before their own read.
  rememberProductPhotos(rows);
  return rows;
}

/**
 * Every product in display order. Retired (archived) products are left out
 * unless asked for: the dashboard needs them to name old orders and to bring
 * one back, nothing else does.
 */
export async function getProducts({ includeArchived = false } = {}) {
  const rows = await readAllProducts();
  return sortProducts(includeArchived ? rows : rows.filter((row) => row.archived !== true));
}

let setupAsked = false;

/**
 * The products for the storefront.
 *
 * An empty catalogue means the shop's existing products have not been moved
 * into it yet — the minutes after the new code goes live. The move can be
 * started by anyone until it has run once (see functions/migration.js), so
 * the first visitor's page asks for it, once, and reads again.
 */
export async function getStorefrontProducts() {
  let rows = await readAllProducts();
  if (rows.length === 0 && !setupAsked) {
    setupAsked = true;
    const result = await setUpCatalogue().catch(() => null);
    if (result?.status === 'done') rows = await readAllProducts();
  }
  return sortProducts(rows.filter((row) => row.archived !== true));
}

export async function getProduct(id) {
  const snap = await getDoc(doc(firestore, 'products', String(id)));
  if (!snap.exists()) return null;
  const row = productRow(snap);
  rememberProductPhotos([row]);
  return row;
}

/**
 * Put in an update to remove that field from the document, so a page can ask
 * for it without reaching into Firestore itself.
 */
export const removedField = () => deleteField();

/**
 * Adds a stock move for one product to a batch: the product's figures change
 * by increments — so two people moving stock at once cannot overwrite each
 * other — and the move is written to the stock history beside it.
 */
function stageStockMove(batch, move, { reason, name, orderId, orderNum, note }) {
  const available = Number(move.available) || 0;
  const reserved = Number(move.reserved) || 0;
  if (available === 0 && reserved === 0) return;
  batch.update(doc(firestore, 'products', String(move.productId)), {
    available: increment(available),
    reserved: increment(reserved),
    total: increment(available + reserved),
    updated_at: serverTimestamp(),
  });
  batch.set(doc(collection(firestore, 'stockMovements')), {
    ...stockMovement({
      productId: move.productId,
      productName: name,
      available,
      reserved,
      reason,
      orderId,
      orderNum,
      note,
      by: actor(),
    }),
    at: serverTimestamp(),
  });
}

/** Line names by product id, for naming stock moves. */
const namesFrom = (...orders) => {
  const names = {};
  orders.forEach((order) => {
    if (!order) return;
    orderLines(order).forEach((line) => {
      names[line.productId] = names[line.productId] || line.name;
    });
  });
  return names;
};

/**
 * Creates a product and returns its id. Its opening stock, if any, is the
 * first entry in its stock history.
 */
export async function createProduct(fields) {
  const ref = doc(collection(firestore, 'products'));
  const available = Number(fields.available) || 0;
  const reserved = Number(fields.reserved) || 0;
  const batch = writeBatch(firestore);
  batch.set(ref, {
    ...fields,
    available,
    reserved,
    total: available + reserved,
    archived: false,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  if (available !== 0 || reserved !== 0) {
    batch.set(doc(collection(firestore, 'stockMovements')), {
      ...stockMovement({
        productId: ref.id,
        productName: itemNameOf(fields),
        available,
        reserved,
        reason: 'opening_balance',
        by: actor(),
      }),
      at: serverTimestamp(),
    });
  }
  await batch.commit();
  return ref.id;
}

/**
 * Saves a product's details. `stock`, when given, is a change to its figures —
 * `{ available, reserved }` to add, not the new totals — so a sale made while
 * the admin was editing is not overwritten by the figure they started from.
 */
export async function updateProduct(id, fields, { stock = null, note = '' } = {}) {
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'products', String(id)), {
    ...fields,
    updated_at: serverTimestamp(),
  });
  if (stock) {
    stageStockMove(
      batch,
      { productId: id, ...stock },
      { reason: 'adjustment', name: itemNameOf(fields), note }
    );
  }
  await batch.commit();
}

/** Puts products in the order given: the first shows first everywhere. */
export async function setProductOrder(ids) {
  const batch = writeBatch(firestore);
  ids.forEach((id, index) => {
    batch.update(doc(firestore, 'products', String(id)), {
      sortOrder: index + 1,
      updated_at: serverTimestamp(),
    });
  });
  await batch.commit();
}

/**
 * Makes a product's figures agree with a stock take: `counted` units on hand,
 * of which the ones reserved for paid orders stay reserved and the rest become
 * available. Read and written in one transaction, so a sale landing meanwhile
 * is taken into account rather than lost.
 */
export async function setStockFromCount(id, counted) {
  const ref = doc(firestore, 'products', String(id));
  return runTransaction(firestore, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That product no longer exists.');
    const product = snap.data();
    const reserved = Number(product.reserved) || 0;
    const before = Number(product.available) || 0;
    const available = counted - reserved;
    tx.update(ref, { available, total: counted, updated_at: serverTimestamp() });
    if (available !== before) {
      tx.set(doc(collection(firestore, 'stockMovements')), {
        ...stockMovement({
          productId: id,
          productName: itemNameOf(product),
          available: available - before,
          reserved: 0,
          reason: 'stock_count',
          note: `${counted} counted across the stock locations`,
          by: actor(),
        }),
        at: serverTimestamp(),
      });
    }
    return { available, reserved, total: counted };
  });
}

/** The most recent stock movements for one product, newest first. */
export async function getStockMovements(productId, max = 100) {
  const snap = await getDocs(
    query(
      collection(firestore, 'stockMovements'),
      where('productId', '==', String(productId)),
      orderBy('at', 'desc'),
      limit(max)
    )
  );
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

//
// ——— STOCK LOCATIONS ———————————————————————————————————————————————————
//
// Where stock is kept, each with a count per product: `counts` is keyed by the
// product id. Admin-only — the storefront never needs to know which shed a
// bale is in.

export async function getStockLocations() {
  const snap = await getDocs(collection(firestore, 'stockLocations'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addStockLocation(name) {
  const ref = await addDoc(collection(firestore, 'stockLocations'), {
    name,
    archived: false,
    counts: {},
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return ref.id;
}

export async function updateStockLocation(id, data) {
  await updateDoc(doc(firestore, 'stockLocations', id), {
    ...data,
    updated_at: serverTimestamp(),
  });
}

/** Writes counted figures, { productId: units }, into one location. */
export async function setStockLocationCounts(id, counts) {
  const fields = {};
  Object.entries(counts).forEach(([productId, units]) => {
    fields[`counts.${productId}`] = units;
  });
  await updateDoc(doc(firestore, 'stockLocations', id), {
    ...fields,
    counted_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

// A Firestore batch holds at most 500 writes. Orders are handed out in groups
// that stay well under it.
const BATCH_WRITES = 400;

/**
 * Hands orders out: marks each fulfilled, releases its units from reserved
 * (logging why), and — with `assignments` — records where its units came from
 * and takes them off those locations. Each group of orders is one batch, so a
 * location or a product can never lose stock for an order that did not get
 * marked, or the reverse.
 *
 * `assignments` is { orderId: [{ productId, locationId, location, units }] };
 * entries with no locationId came from stock that was never placed. Without
 * it, the orders are simply marked fulfilled.
 */
export async function fulfilOrders(orders, assignments = null) {
  const groups = [];
  let group = [];
  let writes = 0;
  orders.forEach((order) => {
    const cost =
      1 + 2 * orderLines(order).length + ((assignments && assignments[order.id]) || []).length;
    if (group.length > 0 && writes + cost > BATCH_WRITES) {
      groups.push(group);
      group = [];
      writes = 0;
    }
    group.push(order);
    writes += cost;
  });
  if (group.length > 0) groups.push(group);

  for (const members of groups) {
    const batch = writeBatch(firestore);
    const taken = {};
    members.forEach((order) => {
      const entries = assignments ? assignments[order.id] || [] : null;
      // fulfilled_at is what makes turnaround measurable; without it the
      // dashboard can only say that an order was fulfilled, not when.
      batch.update(doc(firestore, 'orders', order.id), {
        fulfilled: true,
        fulfilled_at: serverTimestamp(),
        ...(entries ? { fulfilled_from: entries } : {}),
      });
      const names = namesFrom(order);
      stockChange(order, { ...order, fulfilled: true }).forEach((move) =>
        stageStockMove(batch, move, {
          reason: 'fulfilled',
          name: names[move.productId],
          orderId: order.id,
          orderNum: order.orderNum,
        })
      );
      (entries || []).forEach(({ productId, locationId, units }) => {
        if (!locationId) return;
        taken[locationId] = taken[locationId] || {};
        taken[locationId][productId] = (taken[locationId][productId] || 0) + units;
      });
    });
    Object.entries(taken).forEach(([locationId, byProduct]) => {
      const fields = { updated_at: serverTimestamp() };
      Object.entries(byProduct).forEach(([productId, units]) => {
        // Increment rather than read-and-write, so two people handing out from
        // the same shed at once cannot overwrite each other's take.
        fields[`counts.${productId}`] = increment(-units);
      });
      batch.update(doc(firestore, 'stockLocations', locationId), fields);
    });
    await batch.commit();
  }
}

//
// ——— ORDERS ——————————————————————————————————————————————————————————
//

// Orders are now created before payment, so abandoned checkouts sit in the
// collection with paid:false. The dashboard only ever cares about paid ones.
const isPaid = (order) => order.paid === true;

export async function getUnfulfilledOrders() {
  const q = query(
    collection(firestore, 'orders'),
    where('fulfilled', '==', false),
    orderBy('created_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(isPaid);
}

export async function getAllOrders() {
  const q = query(
    collection(firestore, 'orders'),
    orderBy('created_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(isPaid);
}

/**
 * Records an order taken in the dashboard — phone, WhatsApp, cash — and, when
 * it is recorded paid, takes its units out of available into reserved, in one
 * batch. Returns the new order's id.
 */
export async function recordManualOrder(data) {
  const ref = doc(collection(firestore, 'orders'));
  const order = { ...data, stock_applied: data.paid === true };
  const batch = writeBatch(firestore);
  batch.set(ref, { ...order, created_at: serverTimestamp() });
  const names = namesFrom(order);
  stockChange(null, order).forEach((move) =>
    stageStockMove(batch, move, {
      reason: 'manual_order',
      name: names[move.productId],
      orderId: ref.id,
      orderNum: order.orderNum,
    })
  );
  await batch.commit();
  return ref.id;
}

/**
 * Saves a super admin's changes to an order, and moves whatever stock the
 * change implies — more or fewer units, or the order becoming paid or unpaid —
 * in the same batch. See stockChange in catalogue.js.
 */
export async function saveOrderChanges(order, changes) {
  const after = { ...order, ...changes };
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'orders', order.id), changes);
  const names = namesFrom(after, order);
  stockChange(order, after).forEach((move) =>
    stageStockMove(batch, move, {
      reason: 'order_edited',
      name: names[move.productId],
      orderId: order.id,
      orderNum: after.orderNum,
    })
  );
  await batch.commit();
}

/**
 * Deletes an order — a soft delete, it stays in the collection — and hands
 * back whatever stock it was holding: units still waiting to go out return
 * from reserved to available, and units already handed out return to
 * available. An order that never took stock hands nothing back.
 */
export async function deleteOrder(order) {
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'orders', order.id), { deleted: true });
  const names = namesFrom(order);
  stockChange(order, null).forEach((move) =>
    stageStockMove(batch, move, {
      reason: 'order_deleted',
      name: names[move.productId],
      orderId: order.id,
      orderNum: order.orderNum,
    })
  );
  await batch.commit();
}

export async function updateOrder(id, data) {
  await updateDoc(doc(firestore, 'orders', id), data);
}

export async function getOrderById(id) {
  const snap = await getDoc(doc(firestore, 'orders', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * The orders belonging to one signed-in customer, newest first.
 *
 * Two queries rather than one, because an order can be tied to a customer in
 * two ways: the uid stamped on it at checkout, and — for an order captured by
 * hand over the phone — the email address alone. The email query only runs for
 * a verified address, matching firestore.rules, which will reject it otherwise.
 *
 * Deliberately unordered in Firestore: `where` on one field plus `orderBy` on
 * another needs a composite index, and a customer's own history is small enough
 * to sort here. Unpaid checkouts are dropped — an abandoned basket is not an
 * order — as are ones an admin has deleted.
 */
export async function getOrdersForUser(user) {
  if (!user?.uid) return [];

  const queries = [
    getDocs(query(collection(firestore, 'orders'), where('uid', '==', user.uid))),
  ];
  if (user.emailVerified && user.email) {
    queries.push(
      getDocs(query(collection(firestore, 'orders'), where('customer_email', '==', user.email)))
    );
  }

  // One failing must not take the other down with it: a customer whose email
  // query is refused should still see the orders their uid matches.
  const results = await Promise.allSettled(queries);
  const byId = new Map();
  results.forEach((result) => {
    if (result.status !== 'fulfilled') {
      console.error('Error loading orders', result.reason);
      return;
    }
    result.value.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
  });

  return [...byId.values()]
    .filter((order) => order.paid === true && order.deleted !== true)
    .sort((a, b) => createdAtMillis(b.created_at) - createdAtMillis(a.created_at));
}

// Log failed orders when Firestore insertion does not succeed
export async function logFailedOrder(data) {
  await addDoc(collection(firestore, 'failedOrders'), {
    ...data,
    created_at: serverTimestamp(),
  });
}

//
// ——— DISCOUNT CODES ————————————————————————————————————————————————————
//

/**
 * Creates a delivery code.
 *
 * What a code does to a price is four maps, keyed by product id and written
 * together because they are read together (see src/pricing.js):
 *   discountPercents   — a percentage off the ordinary price
 *   discountUnitPrices — a fixed rand price per unit, instead of a percentage
 *   discountTypes      — which of the two applies; absent means percent
 *   discountMaxUnits   — how many units the rule reaches; absent means all of
 *                        them, so "50%, 8 max" charges full price from the 9th
 *
 * `deliveryFee` is a flat rand amount added to the order when the code is
 * applied — the opposite direction to the discount, and independent of it. A
 * code may carry a discount, a delivery charge, both, or neither.
 */
export async function createDiscountCode(
  code,
  discountPercents,
  adminEmail,
  maxUses = 1,
  allowMultipleUsesPerUser = true,
  deliveryFee = 0,
  { discountTypes = {}, discountUnitPrices = {}, discountMaxUnits = {} } = {}
) {
  const fee = Number(deliveryFee);
  await setDoc(
    doc(firestore, 'discountCodes', code),
    {
      code,
      discountPercents,
      discountTypes,
      discountUnitPrices,
      discountMaxUnits,
      deliveryFee: Number.isFinite(fee) && fee > 0 ? fee : 0,
      createdBy: adminEmail,
      maxUses,
      uses: 0,
      allowMultipleUsesPerUser,
      usedByEmails: [],
      used: false,
      created_at: serverTimestamp(),
    }
  );
}

export async function getDiscountCode(code) {
  const snap = await getDoc(doc(firestore, 'discountCodes', code));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function markDiscountCodeUsed(code, userEmail, orderId) {
  const discountRef = doc(firestore, 'discountCodes', code);
  const snap = await getDoc(discountRef);
  if (!snap.exists()) return;
  const data = snap.data();
  const newUses = (data.uses || 0) + 1;
  await updateDoc(discountRef, {
    uses: newUses,
    used: newUses >= (data.maxUses || 1),
    usedByEmail: userEmail,
    usedByEmails: arrayUnion(userEmail),
    used_at: serverTimestamp(),
    usedOrderId: orderId,
  });
}

export async function getAllDiscountCodes() {
  const snap = await getDocs(collection(firestore, 'discountCodes'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Best-effort millisecond value for the various shapes created_at takes. */
function createdAtMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Rebuild a delivery code's redemption record from the orders that actually
 * carry it, and report whether the code exists.
 *
 * Editing an order can move a redemption from one code to another. Rather than
 * trying to keep increments and decrements in step — which drifts the moment
 * one of them fails — the counters are recomputed from the orders themselves,
 * so the record is correct no matter how it got out of step.
 *
 * An order counts as a redemption once it is paid and has not been deleted: a
 * deleted order hands its stock back, so it hands its code use back too.
 */
export async function reconcileDiscountCode(code) {
  if (!code) return true;
  const ref = doc(firestore, 'discountCodes', code);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;

  const ordersSnap = await getDocs(
    query(collection(firestore, 'orders'), where('discountCode', '==', code))
  );
  const redemptions = ordersSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((o) => o.paid === true && o.deleted !== true)
    .sort((a, b) => createdAtMillis(a.created_at) - createdAtMillis(b.created_at));

  const latest = redemptions[redemptions.length - 1] || null;
  const uses = redemptions.length;

  await updateDoc(ref, {
    uses,
    used: uses >= (Number(snap.data().maxUses) || 1),
    usedByEmails: [
      ...new Set(redemptions.map((o) => o.customer_email).filter(Boolean)),
    ],
    usedByEmail: latest?.customer_email || '',
    usedOrderId: latest?.id || '',
    used_at: latest?.created_at || null,
  });

  return true;
}

//
// ——— ADMIN USERS ————————————————————————————————————————————————————
//

const NO_ROLE = { isAdmin: false, isSuperAdmin: false };

/**
 * Read the caller's role from the `admins` collection, whose documents are
 * keyed by email address.
 *
 * `isAdmin: true` is the ordinary admin. `isSuperAdmin: true` additionally
 * grants editing of order details (delivery code, quantities, prices, customer)
 * — an ordinary admin may only flip the fulfilment flags. The same restriction
 * is enforced in firestore.rules, which is what actually protects the data.
 */
export async function getAdminRole(email) {
  if (!email) return NO_ROLE;
  try {
    const snap = await getDoc(doc(firestore, 'admins', email));
    if (!snap.exists()) return NO_ROLE;
    const data = snap.data();
    const superAdmin = data.isSuperAdmin === true;
    // A super admin is an admin regardless of how isAdmin happens to be set.
    return {
      isAdmin: data.isAdmin === true || superAdmin,
      isSuperAdmin: superAdmin,
    };
  } catch (err) {
    console.error('Error checking admin status', err);
    return NO_ROLE;
  }
}

/** Check if the given email belongs to an admin (super admins included). */
export async function isAdmin(email) {
  const role = await getAdminRole(email);
  return role.isAdmin;
}

//
// ——— DASHBOARD READS ————————————————————————————————————————————————
//

/**
 * Every order document, abandoned checkouts included.
 *
 * The storefront helpers above deliberately hide unpaid orders, because an
 * abandoned checkout is not an order. The dashboard needs them anyway: knowing
 * how many checkouts never completed — and being able to prove none of them
 * quietly became an order — is part of watching the payment pipeline. Callers
 * filter for what they need; `paid` and `deleted` are left intact here.
 */
export async function getEveryOrder() {
  const q = query(collection(firestore, 'orders'), orderBy('created_at', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

//
// ——— DELIVERY CODE MAINTENANCE ——————————————————————————————————————
//

export async function updateDiscountCode(code, data) {
  await updateDoc(doc(firestore, 'discountCodes', code), data);
}

export async function deleteDiscountCode(code) {
  await deleteDoc(doc(firestore, 'discountCodes', code));
}

//
// ——— SITE PHOTOS ————————————————————————————————————————————————————————
//
// The site's own photos — the background slideshow behind the home page, and
// the sign-in panel — on one public document. See photos.js.

const SITE_PHOTOS = () => doc(firestore, 'site', 'photos');

/** The `site/photos` document's fields, or {} when nothing has been set. */
export async function getSitePhotos() {
  const snap = await getDoc(SITE_PHOTOS());
  const data = snap.exists() ? snap.data() : {};
  rememberSitePhotos(data);
  return data;
}

/**
 * Writes fields of `site/photos`: a slot's list of entries, or null to go back
 * to its built-in photo, and `heroInterval`, the seconds each slide shows.
 */
export async function saveSitePhotos(fields) {
  const update = { updated_at: serverTimestamp() };
  Object.entries(fields).forEach(([field, value]) => {
    update[field] = value == null ? deleteField() : value;
  });
  await setDoc(SITE_PHOTOS(), update, { merge: true });
  // So this tab's own pages show it without waiting for a reload.
  rememberSitePhotos({ ...rememberedSitePhotos(), ...fields });
}

//
// ——— POPUPS & ANNOUNCEMENTS ————————————————————————————————————————
//

/**
 * Fetch all popups (for admin management).
 */
export async function getAllPopups() {
  try {
    const q = query(collection(firestore, 'popups'), orderBy('created_at', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // If index or ordering issue occurs, fallback to un-ordered query
    const snap = await getDocs(collection(firestore, 'popups'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
}

/**
 * Fetch active popups (for storefront visitors).
 */
export async function getActivePopups() {
  try {
    const q = query(collection(firestore, 'popups'), where('enabled', '==', true));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Error fetching active popups:', err);
    return [];
  }
}

/**
 * Create a new popup.
 */
export async function createPopup(popupData) {
  const docRef = await addDoc(collection(firestore, 'popups'), {
    ...popupData,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Update an existing popup.
 */
export async function updatePopup(id, popupData) {
  await updateDoc(doc(firestore, 'popups', id), {
    ...popupData,
    updated_at: serverTimestamp(),
  });
}

/**
 * Delete a popup.
 */
export async function deletePopup(id) {
  await deleteDoc(doc(firestore, 'popups', id));
}

/**
 * Quick toggle for popup active state.
 */
export async function togglePopup(id, enabled) {
  await updateDoc(doc(firestore, 'popups', id), {
    enabled,
    updated_at: serverTimestamp(),
  });
}

